import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Switch } from 'components/ui/switch';
import { useStepper } from 'components/stepper/stepper-provider';
import { useCreateApiContext } from '../create-api-context';
import { useCreateApiAction } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { useToast } from 'hooks/use-toast';
import { useState, useEffect, useMemo, Fragment, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { JsonCodeEditor } from '../json-code-editor';
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  PlusCircle,
} from 'lucide-react';
import { ParameterFormField } from './utils/tree-utils';
import { useParameterTree } from './hooks/use-parameter-tree';
import { buildParameters, reconstructTree } from './utils/schema-utils';
import { getGraphQLOperationType } from './utils/graphql-utils';
import { PARAMETER_NAME_PLACEHOLDER, validateParameterName } from './utils/naming-utils';
import {
  OUTPUT_PARAMETER_TABLE_COL_SPAN,
  OUTPUT_PARAMETER_TABLE_GRID,
} from './utils/parameter-table-layout';
import { CreateApiParameterFormHeader } from './create-api-parameter-form-header';
import { CreateApiParameterMobileList } from './create-api-parameter-mobile-list';

const nestedObjectLevel = 3;

function getJsonObjectError(
  value: string,
  label: string,
  translate: (key: string, options?: { defaultValue?: string; label?: string }) => string
) {
  try {
    const parsed = JSON.parse(value || '{}');
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
      return translate('JSON_MUST_BE_OBJECT', {
        label,
        defaultValue: '{{label}} must be a JSON object.',
      });
    }

    return '';
  } catch (error) {
    return error instanceof Error
      ? error.message
      : translate('PLEASE_PROVIDE_VALID_JSON', {
          label: label.toLowerCase(),
          defaultValue: 'Please provide valid {{label}}.',
        });
  }
}

export function CreateApiOutputParametersForm({ toolId }: { toolId: string }) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const isBlocksTool = searchParams.get('isBlocksTool') === 'true';
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const projectKey = configurationSettings?.blocksKey || '';
  const queryClient = useQueryClient();
  const { nextStep } = useStepper();
  const { formData, updateFormData } = useCreateApiContext();
  const { mutateAsync: createApiAction, isPending } = useCreateApiAction();
  const { toast } = useToast();
  const [viewMode, setViewMode] = useState<'table' | 'json'>('table');
  const [outputText, setOutputText] = useState('{}');
  const [parameterErrors, setParameterErrors] = useState<Record<string, string>>({});
  const initializedFromRef = useRef('');

  const {
    parameters,
    setParameters,
    handleAddParameter,
    handleAddChildParameter,
    handleRemoveParameter,
    handleParameterChange,
    toggleExpand,
  } = useParameterTree([
    {
      id: '1',
      name: '',
      description: '',
      type: 'string',
      location: 'body',
      defaultValue: '',
      required: false,
    },
  ]);

  const namingErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    const validate = (params: ParameterFormField[]) => {
      params.forEach((p) => {
        const err = validateParameterName(p.name, 'body');
        if (err) errors[p.id] = err.message;
        if (p.children) validate(p.children);
      });
    };
    validate(parameters);
    return errors;
  }, [parameters]);

  const hasNamingErrors = Object.keys(namingErrors).length > 0;

  useEffect(() => {
    const source = formData.output_parameters;

    if (!source || Object.keys(source).length === 0) {
      return;
    }

    const fingerprint = JSON.stringify(source);
    if (initializedFromRef.current === fingerprint) {
      return;
    }
    initializedFromRef.current = fingerprint;

    try {
      const loadedParams = reconstructTree(source, {
        forceBodyLocation: true,
      });
      if (loadedParams.length > 0) {
        setParameters(loadedParams);
      }
      setOutputText(JSON.stringify(source, null, 2));
    } catch (e) {
      console.error('Failed to load output parameters', e);
    }
  }, [formData.output_parameters, setParameters]);

  const outputParametersLabel = t('OUTPUT_PARAMETERS') || 'Output parameters';

  const validationError = useMemo(
    () => (viewMode === 'json' ? getJsonObjectError(outputText, outputParametersLabel, t) : ''),
    [outputParametersLabel, outputText, t, viewMode],
  );

  const handleSubmit = async () => {
    let outputParameters;

    if (viewMode === 'table') {
      const validParams = parameters.filter((p) => p.name && p.name.trim() !== '');
      const uniqueNames = new Set(validParams.map((p) => p.name));

      if (uniqueNames.size !== validParams.length) {
        toast({
          variant: 'destructive',
          title: t('DUPLICATE_PARAMETERS') || 'Duplicate parameters',
          description: t('PARAMETER_NAMES_MUST_BE_UNIQUE') || 'Parameter names must be unique',
        });
        return;
      }

      setParameterErrors({});
      outputParameters = buildParameters(parameters, true);
    } else {
      try {
        const parsed = JSON.parse(outputText || '{}');
        if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
          throw new Error(
            t('OUTPUT_PARAMETERS_MUST_BE_JSON_OBJECT') ||
              'Output parameters must be a JSON object.'
          );
        }
        outputParameters = parsed;
      } catch (error) {
        toast({
          variant: 'destructive',
          title: t('INVALID_JSON') || 'Invalid JSON',
          description:
            error instanceof Error
              ? error.message
              : t('VALID_JSON_OBJECT_REQUIRED') || 'Please provide a valid JSON object.',
        });
        return;
      }
    }

    updateFormData({ output_parameters: outputParameters });

    try {
      const isGraphQL = (formData.method || '').toUpperCase() === 'GRAPHQL';
      const payload = {
        name: formData.name,
        description: formData.description,
        method: formData.method,
        endpoint: formData.endpoint,
        input_parameters: formData.input_parameters,
        output_parameters: outputParameters,
        is_open_tool: isBlocksTool,
        ...(isGraphQL
          ? {
              graphql_query: formData.graphql_query || '',
              graphql_operation_type:
                formData.graphql_operation_type ||
                getGraphQLOperationType(formData.graphql_query || ''),
            }
          : {}),
      };

      const response = await createApiAction({
        toolId,
        actionId: formData.actionId,
        projectKey,
        payload,
      });

      if (!response.is_success) {
        throw new Error(
          response.message ||
            response.detail ||
            t('FAILED_TO_SAVE_API_ACTION') ||
            'Failed to save API action.'
        );
      }

      const nextActionId = response.item_id || formData.actionId;
      if (nextActionId && nextActionId !== formData.actionId) {
        updateFormData({ actionId: nextActionId });
      }

      queryClient.invalidateQueries({ queryKey: ['tools', 'detail', toolId] });
      toast({
        variant: 'success',
        title: formData.actionId
          ? t('ACTION_UPDATED') || 'Action updated'
          : t('ACTION_CREATED') || 'Action created',
        description:
          response.detail ||
          t('API_ACTION_SAVED_SUCCESSFULLY') ||
          'The API action has been saved successfully.',
      });

      nextStep();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ERROR') || 'Error',
        description:
          error instanceof Error
            ? error.message
            : t('FAILED_TO_SAVE_API_ACTION') || 'Failed to save API action.',
      });
    }
  };

  const switchToJsonMode = () => {
    const outputParameters = buildParameters(parameters, true);
    setOutputText(JSON.stringify(outputParameters, null, 2));
    setViewMode('json');
  };

  const switchToTableMode = () => {
    try {
      const parsed = JSON.parse(outputText || '{}');
      if (parsed && !Array.isArray(parsed) && typeof parsed === 'object') {
        const loadedParams = reconstructTree(parsed, { forceBodyLocation: true });
        if (loadedParams.length > 0) {
          setParameters(loadedParams);
        }
      }
      setViewMode('table');
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('INVALID_JSON') || 'Invalid JSON',
        description:
          t('CANNOT_SWITCH_TO_TABLE_MODE') ||
          'Cannot switch to table mode. Please fix JSON errors first.',
      });
    }
  };

  const renderRows = (
    params: ParameterFormField[],
    level = 0,
    objectDepth = 0,
    parentType = '',
  ) => {
    return params.map((param, index) => {
      const isArrayItem = parentType === 'array';

      const hasNestedChildren = param.type === 'array' || param.type === 'object';

      return (
        <Fragment key={param.id}>
          {level === 0 && index > 0 ? (
            <div className={`${OUTPUT_PARAMETER_TABLE_COL_SPAN} border-t border-border`} />
          ) : null}
          <div className="contents">
            <div
              className="flex items-start gap-2"
              style={{ paddingLeft: level > 0 ? `${level * 40}px` : undefined }}
            >
              <span className="inline-flex h-8 w-8 shrink-0 items-start justify-center">
                {hasNestedChildren ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 p-0 mt-0.5"
                    onClick={() => toggleExpand(param.id)}
                  >
                    {param.isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                ) : null}
              </span>
              <div className="min-w-0 flex-1 space-y-1">
                <Input
                  placeholder={
                    isArrayItem
                      ? t('ARRAY_ITEM') || 'Array Item'
                      : t('PARAMETER_NAME_PLACEHOLDER') || PARAMETER_NAME_PLACEHOLDER
                  }
                  value={isArrayItem ? '' : param.name}
                  disabled={isArrayItem}
                  onChange={(e) => handleParameterChange(param.id, 'name', e.target.value)}
                  className="h-8"
                />
                {/* {parameterErrors[param.id] && (
                  <p className="text-xs text-destructive">{parameterErrors[param.id]}</p>
                )}
                {namingErrors[param.id] && (
                  <p className="text-xs text-destructive">{namingErrors[param.id]}</p>
                )}
                {!isArrayItem && param.name.length >= 100 && (
                  <p className="text-xs text-destructive">
                    {t('MAX_100_CHARACTERS') || 'Max 100 characters'}
                  </p>
                )} */}
              </div>
            </div>
            <div className="space-y-1">
              <Input
                placeholder={t('ENTER_DESCRIPTION') || 'Enter description'}
                value={param.description}
                onChange={(e) => handleParameterChange(param.id, 'description', e.target.value)}
                className="h-8"
              />
              {/* {(param.description || '').length >= 100 && (
                <p className="text-xs text-destructive">
                  {t('MAX_100_CHARACTERS') || 'Max 100 characters'}
                </p>
              )} */}
            </div>
            <div>
              <Select
                value={param.type}
                onValueChange={(value) => handleParameterChange(param.id, 'type', value)}
              >
                <SelectTrigger className="h-8">
                  <SelectValue placeholder={t('SELECT') || 'Select'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="string">{t('STRING') || 'String'}</SelectItem>
                  <SelectItem value="number">{t('NUMBER') || 'Number'}</SelectItem>
                  <SelectItem value="integer">{t('INTEGER') || 'Integer'}</SelectItem>
                  <SelectItem value="boolean">{t('BOOLEAN') || 'Boolean'}</SelectItem>
                  {objectDepth < nestedObjectLevel && (
                    <SelectItem value="object">{t('OBJECT') || 'Object'}</SelectItem>
                  )}
                  {level < 10 && !isArrayItem && (
                    <SelectItem value="array">{t('ARRAY') || 'Array'}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center">
              <Switch
                checked={param.required}
                onCheckedChange={(checked) => handleParameterChange(param.id, 'required', checked)}
              />
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleRemoveParameter(param.id)}
                disabled={isArrayItem}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              {param.type === 'object' && level < 10 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleAddChildParameter(param.id, 'body')}
                  className="text-primary"
                >
                  <PlusCircle className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {(param.type === 'object' || param.type === 'array') &&
            param.isExpanded &&
            param.children &&
            renderRows(
              param.children,
              level + 1,
              param.type === 'object' ? objectDepth + 1 : objectDepth,
              param.type,
            )}
        </Fragment>
      );
    });
  };

  return (
    <div className="w-full min-w-0">
      <CreateApiParameterFormHeader
        title={t('OUTPUT_PARAMETERS') || 'Output Parameters'}
        description={
          t('CREATE_API_OUTPUT_PARAMETERS_DESCRIPTION') ||
          'Describe the output format and data your API sends back.'
        }
        viewMode={viewMode}
        onTableView={switchToTableMode}
        onJsonView={switchToJsonMode}
      />

      {viewMode === 'table' ? (
        <>
          <div className="w-full min-w-0 space-y-4">
            <CreateApiParameterMobileList
              variant="output"
              parameters={parameters}
              parameterErrors={parameterErrors}
              namingErrors={namingErrors}
              onToggleExpand={toggleExpand}
              onParameterChange={handleParameterChange}
              onRemoveParameter={handleRemoveParameter}
              onAddChildParameter={handleAddChildParameter}
            />

            <div className="hidden w-full min-w-0 overflow-x-auto rounded-lg bg-card/50 p-4 md:block">
              <div
                className={`grid ${OUTPUT_PARAMETER_TABLE_GRID} items-start gap-x-4 gap-y-3 min-w-max`}
              >
                <div className="text-sm font-medium text-muted-foreground">{t('NAME') || 'Name'}</div>
                <div className="text-sm font-medium text-muted-foreground">
                  {t('DESCRIPTION') || 'Description'}
                </div>
                <div className="text-sm font-medium text-muted-foreground">{t('TYPE') || 'Type'}</div>
                <div className="text-sm font-medium text-muted-foreground">
                  {t('REQUIRED') || 'Required'}
                </div>
                <div />
                <div className={`${OUTPUT_PARAMETER_TABLE_COL_SPAN} border-b border-border pb-3`} />
                {renderRows(parameters)}
              </div>
            </div>

            <div>
              <Button variant="ghost" onClick={handleAddParameter} className="text-primary">
                <Plus className="mr-2 h-4 w-4" />
                {t('ADD_PARAMETER') || 'Add Parameter'}
              </Button>
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-6">
          <div className="space-y-3">
            <Label htmlFor="output-parameters">
              {t('OUTPUT_PARAMETERS_JSON') || 'Output Parameters JSON'}
            </Label>
            <JsonCodeEditor
              id="output-parameters"
              value={outputText}
              onChange={setOutputText}
              placeholder='{"customer":{"Type":"object","Required":true}}'
              hasError={Boolean(validationError)}
            />
            {validationError ? (
              <p className="text-sm text-destructive">{validationError}</p>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('OUTPUT_PARAMETERS_JSON_HINT') ||
                  'Use a JSON object keyed by output field name. Validation happens as you type.'}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 flex items-center justify-start gap-3">
        <Button
          onClick={handleSubmit}
          disabled={
            isPending || (viewMode === 'table' ? hasNamingErrors : Boolean(validationError))
          }
          className="w-full px-8 sm:w-auto"
        >
          {isPending ? t('DEBUGGING') || 'Debugging...' : t('DEBUG') || 'Debug'}
        </Button>
      </div>
    </div>
  );
}
